<script setup>
import { computed, defineComponent, h, markRaw } from "vue";
import { Icon } from "@iconify/vue";

const appStore = useAppStore();

const InlineSvgIcon = markRaw(
  defineComponent({
    name: "AlertInlineSvgIcon",
    props: {
      svg: {
        type: String,
        required: true,
      },
    },
    setup(props, { attrs }) {
      return () => {
        const { class: className, ...restAttrs } = attrs;
        return h("span", {
          ...restAttrs,
          class: [
            "tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-text-inherit [&>svg]:tw-block [&>svg]:tw-h-full [&>svg]:tw-w-full",
            className,
          ],
          innerHTML: props.svg,
        });
      };
    },
  })
);

const resolveAlertIcon = (alert) => {
  if (!alert) return {};

  const { icon, iconComponent, iconSvg, iconProps } = alert;

  if (iconSvg) {
    return {
      component: InlineSvgIcon,
      props: {
        svg: iconSvg,
        ...(iconProps || {}),
      },
    };
  }

  if (iconComponent) {
    return {
      component: iconComponent,
      props: iconProps || {},
    };
  }

  if (!icon) {
    return {};
  }

  if (typeof icon === "string") {
    const trimmed = icon.trim();
    if (trimmed.startsWith("<svg")) {
      return {
        component: InlineSvgIcon,
        props: {
          svg: trimmed,
          ...(iconProps || {}),
        },
      };
    }
    return {
      iconName: trimmed,
    };
  }

  if (typeof icon === "object") {
    if (icon?.svg) {
      return {
        component: InlineSvgIcon,
        props: {
          svg: icon.svg,
          ...(icon.props || {}),
        },
      };
    }

    if (icon?.component) {
      return {
        component: icon.component,
        props: icon.props || iconProps || {},
      };
    }

    return {
      component: icon,
    };
  }

  return {};
};

const alerts = computed(() =>
  appStore.appAlerts.map((alert) => ({
    ...alert,
    __iconMeta: resolveAlertIcon(alert),
  }))
);

const copyAlertValue = async (value) => {
  if (!value) return;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  } catch (error) {
    console.error("Failed to copy alert value:", error);
  }
};
</script>

<template>
  <div
    class="alert-container tw-fixed tw-top-3 tw-right-3 tw-z-[9999] tw-max-w-[400px]"
  >
    <v-alert
      v-for="(alert, index) in alerts"
      :key="index"
      :type="alert.type || undefined"
      :color="alert.color"
      :icon="alert.__iconMeta.iconName || undefined"
      :closable="alert.closable"
      @click:close="appStore.removeAlert(index)"
      border="start"
      elevation="2"
      class="mb-3 tw-break-words"
      :class="`app-alert alert-${index}`"
      theme="dark"
      variant="tonal"
    >
      <template v-if="alert.__iconMeta.component" #prepend>
        <component
          :is="alert.__iconMeta.component"
          v-bind="alert.__iconMeta.props"
        />
      </template>
      <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
        <span class="tw-break-words tw-whitespace-pre-line">
          {{ alert.message }}
        </span>
        <VBtn
          v-if="alert.copyText"
          icon
          variant="text"
          size="x-small"
          color="white"
          class="tw-flex-shrink-0"
          :aria-label="$t ? $t('copy') : 'Copy'"
          @click="copyAlertValue(alert.copyText)"
        >
          <Icon icon="mdi:content-copy" class="tw-h-4 tw-w-4" />
        </VBtn>
      </div>
    </v-alert>
  </div>
</template>
